// Media upload to the public repo. Two destinations by size
// (05_TWO_REPO_MODEL.md → binary-asset strategy):
//   ≤ 100KB  → committed to assets/inline/ via the Contents API (small,
//              keeps the repo browsable, served straight off CF Pages).
//   > 100KB  → uploaded as a GitHub Release asset (repos hate big files;
//              Releases love them — durable CDN URL, Accept-Ranges so
//              <video>/<audio> scrub natively, 2GB/file).
//
// Browser-direct (api.github.com is CORS-friendly). The release upload
// hits uploads.github.com, which is also CORS-open for authenticated
// requests. Idempotent by content sha — re-uploading an identical file
// reuses the existing release tag (asset-<sha8>).

import { GithubError } from "./github";

const API = "https://api.github.com";
const UPLOADS = "https://uploads.github.com";
const INLINE_THRESHOLD = 100 * 1024; // 100KB

export interface MediaRef {
  /** "image" | "video" | "audio" | "file" — derived from the mime. */
  kind: "image" | "video" | "audio" | "file";
  /** Public URL the post references. For inline: a site-relative path
   *  (assets/inline/<name>); for releases: an absolute github.com URL. */
  url: string;
  mime: string;
  size: number;
  filename: string;
  /** Where it landed — for UI + ref-counting later. */
  storage: "inline" | "release";
}

function ghHeaders(token: string, extra?: Record<string, string>): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
    ...extra,
  };
}

async function ghErr(res: Response, what: string): Promise<never> {
  let msg = `${what} failed (${res.status})`;
  try { const j = await res.json() as { message?: string }; if (j.message) msg = j.message; } catch { /* ignore */ }
  throw new GithubError(msg, res.status);
}

export function mediaKind(mime: string): MediaRef["kind"] {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "file";
}

/** Slugify a filename to keep URLs + paths clean, preserving extension. */
export function safeFilename(name: string): string {
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot).toLowerCase().replace(/[^a-z0-9.]/g, "") : "";
  const base = (dot > 0 ? name.slice(0, dot) : name)
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "file";
  return base + ext;
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function bytesToBase64(bytes: Uint8Array): string {
  // Chunked to avoid call-stack limits on large-ish inline files.
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

/** Upload a file to the public repo, routing by size. Returns the
 *  reference a post should embed. */
export async function uploadMedia(token: string, publicRepo: string, file: File): Promise<MediaRef> {
  const [owner, name] = publicRepo.split("/");
  if (!owner || !name) throw new Error(`invalid repo "${publicRepo}"`);
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const mime = file.type || "application/octet-stream";
  const kind = mediaKind(mime);
  const fname = safeFilename(file.name);

  if (bytes.length <= INLINE_THRESHOLD) {
    // Commit to assets/inline/<sha8>-<name> via the Contents API.
    const sha8 = (await sha256Hex(buf)).slice(0, 8);
    const path = `assets/inline/${sha8}-${fname}`;
    const res = await fetch(`${API}/repos/${owner}/${name}/contents/${path}`, {
      method: "PUT",
      headers: ghHeaders(token, { "Content-Type": "application/json" }),
      body: JSON.stringify({
        message: `BKA: add media ${fname}`,
        content: bytesToBase64(bytes),
        branch: "main",
      }),
    });
    // 422 = already exists at this path (identical content sha) — fine.
    if (!res.ok && res.status !== 422) await ghErr(res, "inline upload");
    return { kind, url: path, mime, size: bytes.length, filename: fname, storage: "inline" };
  }

  // Large file → GitHub Release asset. Tag is content-addressed so
  // re-uploading the same file dedupes to one release.
  const sha8 = (await sha256Hex(buf)).slice(0, 8);
  const tag = `asset-${sha8}`;

  // Find-or-create the release for this tag.
  let releaseId: number | null = null;
  const got = await fetch(`${API}/repos/${owner}/${name}/releases/tags/${tag}`, { headers: ghHeaders(token) });
  if (got.ok) {
    releaseId = (await got.json() as { id: number }).id;
  } else if (got.status === 404) {
    const made = await fetch(`${API}/repos/${owner}/${name}/releases`, {
      method: "POST",
      headers: ghHeaders(token, { "Content-Type": "application/json" }),
      body: JSON.stringify({ tag_name: tag, name: tag, body: `BKA media asset (${fname})`, draft: false, prerelease: false }),
    });
    if (!made.ok) await ghErr(made, "create release");
    releaseId = (await made.json() as { id: number }).id;
  } else {
    await ghErr(got, "lookup release");
  }

  // Upload the asset. If an asset with this name already exists on the
  // release (a prior identical upload), the download URL is stable, so
  // a 422 here is treated as success.
  const upRes = await fetch(
    `${UPLOADS}/repos/${owner}/${name}/releases/${releaseId}/assets?name=${encodeURIComponent(fname)}`,
    { method: "POST", headers: ghHeaders(token, { "Content-Type": mime }), body: bytes },
  );
  if (!upRes.ok && upRes.status !== 422) await ghErr(upRes, "release asset upload");

  const url = `https://github.com/${owner}/${name}/releases/download/${tag}/${fname}`;
  return { kind, url, mime, size: bytes.length, filename: fname, storage: "release" };
}

/** The markdown a creator's editor should insert to reference a media
 *  item. Images embed; everything else becomes a download link. */
export function mediaMarkdown(ref: MediaRef): string {
  if (ref.kind === "image") return `![${ref.filename}](${ref.url})`;
  if (ref.kind === "video") return `\n<video src="${ref.url}" controls width="100%"></video>\n`;
  if (ref.kind === "audio") return `\n<audio src="${ref.url}" controls></audio>\n`;
  return `[${ref.filename}](${ref.url})`;
}
